// frontend/src/charts/ChartCanvas.jsx
// Thin React wrapper around ECharts (no echarts-for-react dependency).
// Loads the engine chunk on first use, keeps the chart sized to its box,
// and disposes it on unmount.
import React, { useEffect, useRef, useState } from 'react';

let enginePromise = null;
export function loadEngine() {
  if (!enginePromise) enginePromise = import('./echartsEngine').then((m) => m.default);
  return enginePromise;
}

export default function ChartCanvas({ option, height = 320 }) {
  const boxRef = useRef(null);
  const chartRef = useRef(null);
  const optionRef = useRef(option);   // latest option, for when the engine finishes loading
  optionRef.current = option;
  const [failed, setFailed] = useState(false);

  // Create once, dispose on unmount.
  useEffect(() => {
    let cancelled = false;
    let observer = null;
    loadEngine()
      .then((echarts) => {
        if (cancelled || !boxRef.current) return;
        const chart = echarts.init(boxRef.current, null, { renderer: 'canvas' });
        chartRef.current = chart;
        if (optionRef.current) chart.setOption(optionRef.current, { notMerge: true });
        observer = new ResizeObserver(() => chart.resize());
        observer.observe(boxRef.current);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (observer) observer.disconnect();
      if (chartRef.current) chartRef.current.dispose();
      chartRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Replace the whole option on every change (no stale series left behind).
  useEffect(() => {
    if (chartRef.current && option) chartRef.current.setOption(option, { notMerge: true });
  }, [option]);

  if (failed) {
    return (
      <div style={{ height }} className="flex items-center justify-center text-sm text-[color:var(--chart-text-muted)]">
        The chart library failed to load. Refresh the page to try again.
      </div>
    );
  }
  // ECharts' aria module writes a generated description (type, series,
  // values) onto this element, so no aria-label is set here.
  return <div ref={boxRef} style={{ height, width: '100%' }} />;
}